import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { fetchEntry, updateEntry, suggestEdit } from '../lib/bibliography.js';
import EntryForm from '../components/bibliography/EntryForm.jsx';

export default function BibliographyEdit() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAdmin, isActive } = useAuth();
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  // `t` deliberately excluded — see ScholarsEdit for the rationale.
  useEffect(() => {
    let cancelled = false;
    fetchEntry(id)
      .then((row) => {
        if (cancelled) return;
        if (!row) setError(t('bibliography.form.notFound'));
        else setEntry(row);
      })
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!isActive) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32">
        <div className="cta-box">
          <h1 className="cta-title">{t('bibliography.form.notActiveTitle')}</h1>
          <p className="cta-body">{t('bibliography.form.notActiveBody')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-ink-secondary">{error}</p>
      </div>
    );
  }

  if (!entry) {
    return (
      <div role="status" aria-live="polite" className="mx-auto max-w-3xl px-8 pb-20 pt-32 text-center text-ink-secondary">
        {t('common.loading')}
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="mx-auto max-w-2xl px-8 pb-20 pt-32">
        <div className="cta-box">
          <div className="section-label">
            {isAdmin
              ? t('bibliography.form.savedLabel')
              : t('bibliography.form.suggestionSentLabel')}
          </div>
          <h1 className="cta-title">{entry.title}</h1>
          <p className="cta-body">
            {isAdmin
              ? t('bibliography.form.savedBody')
              : t('bibliography.form.suggestionSentBody')}
          </p>
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate('/bibliography')}
          >
            {t('bibliography.form.backToBibliography')}
          </button>
        </div>
      </div>
    );
  }

  async function handleSubmit(form, reason) {
    setBusy(true);
    try {
      if (isAdmin) {
        await updateEntry(entry.id, form);
      } else {
        await suggestEdit(entry, form, { authorId: user.id, reason });
      }
      setSubmitted(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 pb-20 pt-28">
      <header className="mb-8">
        <div className="section-label">
          {isAdmin
            ? t('bibliography.form.editLabel')
            : t('bibliography.form.suggestEditLabel')}
        </div>
        <h1 className="font-serif text-3xl font-semibold text-cream">{entry.title}</h1>
        <p className="mt-2 text-sm text-ink-secondary">
          {isAdmin
            ? t('bibliography.form.editSubheadingAdmin')
            : t('bibliography.form.editSubheadingCollaborator')}
        </p>
      </header>

      <EntryForm
        initialValue={entry}
        submitLabel={
          isAdmin
            ? t('bibliography.form.save')
            : t('bibliography.form.submitSuggestion')
        }
        showReason={!isAdmin}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/bibliography')}
        busy={busy}
      />
    </div>
  );
}
